/**
 * FlashBook — the creator's voice, as a number.
 *
 * A recording screen that only counts seconds tells the creator nothing about
 * whether the thing they are doing is *working*: a muted microphone, a phone
 * held too far away, and the wrong input device all look identical to a timer.
 * What makes a recording screen feel alive is the level, and it is also the only
 * honest way to answer the one question they have — "is it hearing me?".
 *
 * Two platforms can answer it, in two different currencies, so the conversion
 * lives here as arithmetic rather than in either of them:
 *
 *  - a **device** reports decibels relative to full scale (`metering`, from
 *    `expo-audio`), where silence sits near −60 and a close voice near −12;
 *  - a **browser** hands out raw samples, and the level is the RMS of a window of
 *    them.
 *
 * Both end at the same 0…1, and both are shaped the same way, because a meter is
 * not a measurement to be read out — it is a *picture of a voice*, and a linear
 * scale spends most of its length on levels nobody reaches and hides the
 * difference between speaking and whispering in the first millimetre.
 */
import {createPlatformMicLevelSource} from '#/lib/flashbookMicLevelImpl'

/** How many bars the meter shows. */
export const METER_BARS = 28

/**
 * Where the meter considers a room quiet.
 *
 * Below this the bar is a resting line rather than a wobble: a laptop fan and a
 * fridge both register, and a meter that flutters on them looks broken.
 */
const SILENCE = 0.06

/** What counts as "the creator is speaking" rather than "there is a room". */
export const VOICE_FLOOR = 0.16

/**
 * A raw sample window's loudness, as the meter should show it.
 *
 * The floor subtraction is what makes the meter look like it is listening rather
 * than buzzing: `SILENCE` of RMS is treated as nothing, and what is left is
 * stretched over the space above it, with a mild curve so the ordinary middle of
 * a voice's range occupies most of the bar's height.
 */
export function levelFromRms(rms: number): number {
  if (!Number.isFinite(rms) || rms <= 0) return 0
  const above = (rms - SILENCE) / (1 - SILENCE)
  if (above <= 0) return 0
  return Math.min(1, Math.pow(above, 0.62))
}

/**
 * Where a device's scale begins and ends, in the decibels it reports.
 *
 * `-50` is a room with something humming in it and `-6` is a voice close to the
 * microphone; a muted input reports the bottom of the scale or `-Infinity`, and
 * either has to look exactly like nothing at all.
 */
const DB_FLOOR = -50
const DB_CEILING = -6

/**
 * A device's metering, as the meter should show it.
 *
 * **Not** the same curve as `levelFromRms`, and deliberately so: the two
 * currencies are distributed differently. A browser hands out raw samples whose
 * RMS for speech is a small fraction, while a device reports what a *voice* looks
 * like on a decibel scale — where speaking sits around `-25` and a room around
 * `-50`. Running a device's reading through the sample curve made a normal
 * speaking voice fill a seventh of the meter, which is the one thing a level
 * meter must never do: it has to be obvious that the creator is being heard.
 */
export function levelFromDbfs(dbfs: number | null | undefined): number {
  if (dbfs === null || dbfs === undefined || !Number.isFinite(dbfs)) return 0
  // `-Infinity` is a real reading — a digital zero — and it means silence.
  const db = Math.max(DB_FLOOR, Math.min(DB_CEILING, dbfs))
  const above = (db - DB_FLOOR) / (DB_CEILING - DB_FLOOR)
  return Math.pow(above, 0.85)
}

/**
 * The bar heights to draw, newest last.
 *
 * A meter is a *history*: one bar that jumps is a volume knob, and a row of them
 * shifting leftwards is a waveform — the difference between a number and the
 * sense that something is being said. New levels arrive at the right, and the
 * oldest is dropped, so the picture always covers the same span of time.
 */
export function pushMeterBar(bars: number[], level: number): number[] {
  const next = [...bars, Math.max(0, Math.min(1, level))]
  return next.length > METER_BARS ? next.slice(next.length - METER_BARS) : next
}

/** An empty meter, so the first frames do not draw a shape that was never heard. */
export function emptyMeter(): number[] {
  return []
}

/** Whether the creator is speaking, as opposed to the room being audible. */
export function isVoicePresent(level: number): boolean {
  return level >= VOICE_FLOOR
}

/**
 * A voice usually rises and falls faster than a meter should.
 *
 * Attack is immediate — a meter that lags behind a syllable is a meter that looks
 * broken — and release is slower, which is what a physical VU movement does and
 * what makes the picture read as speech rather than as noise.
 */
export function easeMeter(previous: number, next: number): number {
  return next >= previous ? next : previous + (next - previous) * 0.35
}

/** What a platform has to provide to feed the meter. */
export type MicLevelSource = {
  /** False where measuring would cost the recording itself (see the impls). */
  supported: boolean
  /**
   * Starts measuring. Returns the release for it, which stops the capture and
   * closes the audio graph — a meter that outlives the card it belonged to is a
   * microphone left open.
   */
  start: (onLevel: (level: number) => void) => () => void
}

/**
 * The platform's own source.
 *
 * `readDbfs` is how a device is asked for a level: the recorder is the thing
 * holding the microphone there, so the level has to come from it. A browser has
 * no such reader — it measures the samples itself — and ignores the argument.
 */
export function createMicLevelSource(
  readDbfs?: () => number | null | undefined,
): MicLevelSource {
  return createPlatformMicLevelSource(readDbfs)
}
